import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

interface ExitDemoConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void; // Handles cancel/X/Escape
  onConfirmPermanent: () => void; // Handles YES (permanently turn off)
  onConfirmTemporary: () => void; // Handles NO (just exit for now)
  isDark?: boolean;
}

export function ExitDemoConfirmationModal({
  isOpen,
  onClose,
  onConfirmPermanent,
  onConfirmTemporary,
  isDark,
}: ExitDemoConfirmationModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dark =
    typeof isDark === 'boolean'
      ? isDark
      : typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

      {/* Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={`relative w-full max-w-md transform overflow-hidden rounded-2xl border p-6 shadow-2xl transition-all ${
          dark
            ? 'border-zinc-800 bg-[#1c1c1e] text-zinc-100'
            : 'border-zinc-200 bg-white text-zinc-950'
        }`}
      >
        {/* Close Button X */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={`absolute right-4 top-4 rounded-full p-1 transition-colors ${
            dark ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'
          }`}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Modal Header */}
        <div className="mb-4">
          <h3
            id="modal-title"
            className="text-lg font-bold tracking-tight"
          >
            Exit Demo Mode?
          </h3>
        </div>

        {/* Modal Body */}
        <div className="mb-6">
          <p className={`text-sm ${dark ? 'text-zinc-300' : 'text-zinc-600'}`}>
            Do you want to permanently turn off Demo Mode for this account?
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onConfirmTemporary}
            className={`flex h-10 items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              dark
                ? 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'
                : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
            }`}
          >
            No — Just exit for now
          </button>
          <button
            type="button"
            onClick={onConfirmPermanent}
            className="flex h-10 items-center justify-center rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
          >
            Yes — Turn off permanently
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
